// The view out of the saloon car's windows: deep space with planets and moons sliding aft, so it
// reads at a glance that the train is underway. A small diorama around the car (a 150 m star dome plus a
// handful of bodies streaming past), drawn only while we're indoors; the real world beyond is
// hidden behind the dome's depth.
//
// The bodies come from makeBody(), a placeholder procedural set until the "Day to night sky" lane's
// planet assets can be dropped in behind the same call.

import * as THREE from 'three';
import { CAR } from '../../sim/saloon';

/** inside the nearest island edge (~190 m from the car), so the dome's depth hides the world */
const DOME_R = 150;
/** train speed as the window sees it (m/s): fast enough to feel underway, slow enough to admire */
const SPEED = 9;
/** bodies wrap along the car's axis over this span */
const SPAN = 150;

type Kind = 'giant' | 'ringed' | 'moon' | 'ice';

const bodyVert = /* glsl */`
  varying vec3 vN; varying vec3 vP; varying vec3 vW;
  void main() {
    vN = normalize(normalMatrix * normal); vP = position;
    vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz;
    gl_Position = projectionMatrix * viewMatrix * w;
  }`;

const bodyFrag = /* glsl */`
  uniform vec3 uA; uniform vec3 uB; uniform vec3 uC; uniform float uBands; uniform float uCraters;
  uniform vec3 uSunDir; uniform float uSeed; uniform float uTime;
  varying vec3 vN; varying vec3 vP; varying vec3 vW;
  float h(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
  float n3(vec3 p) { vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(h(i), h(i + vec3(1,0,0)), f.x), mix(h(i + vec3(0,1,0)), h(i + vec3(1,1,0)), f.x), f.y),
               mix(mix(h(i + vec3(0,0,1)), h(i + vec3(1,0,1)), f.x), mix(h(i + vec3(0,1,1)), h(i + vec3(1,1,1)), f.x), f.y), f.z); }
  void main() {
    vec3 p = normalize(vP);
    float warp = n3(p * 3.0 + uSeed) * 0.6 + n3(p * 9.0 + uSeed) * 0.25;
    // gas bands (latitude + turbulence) or a cratered crust
    float band = sin((p.y + warp * 0.18) * uBands + uSeed) * 0.5 + 0.5;
    vec3 col = mix(uA, uB, band);
    col = mix(col, uC, smoothstep(0.55, 0.9, n3(p * 5.0 + uSeed * 2.0 + vec3(uTime * 0.01, 0.0, 0.0))) * 0.6);
    if (uCraters > 0.0) {
      float c = smoothstep(0.62, 0.7, n3(p * 7.0 + uSeed)) - smoothstep(0.7, 0.8, n3(p * 7.0 + uSeed)) * 0.6;
      col *= 1.0 - c * uCraters * 0.5;
    }
    vec3 n = normalize(vN);
    vec3 l = normalize((viewMatrix * vec4(uSunDir, 0.0)).xyz);
    float lit = smoothstep(-0.15, 0.6, dot(n, l));
    // golden-hour terminator and a thin atmosphere rim
    vec3 c2 = col * (0.06 + 1.05 * lit) + vec3(1.0, 0.55, 0.25) * pow(1.0 - abs(dot(n, l)), 6.0) * lit * 0.25;
    float rim = pow(1.0 - max(0.0, n.z), 3.0);
    c2 += mix(uB, vec3(1.0, 0.8, 0.6), 0.5) * rim * (0.15 + 0.5 * lit);
    gl_FragColor = vec4(c2, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }`;

const ringFrag = /* glsl */`
  uniform vec3 uA; uniform vec3 uB; varying vec2 vUv;
  void main() {
    float r = length(vUv - 0.5) * 2.0;
    if (r < 0.58 || r > 1.0) discard;
    float b = sin(r * 70.0) * 0.5 + 0.5;
    float a = smoothstep(0.58, 0.64, r) * smoothstep(1.0, 0.92, r) * (0.35 + 0.5 * b);
    gl_FragColor = vec4(mix(uA, uB, b) * 0.9, a);
    #include <colorspace_fragment>
  }`;

const SUN = new THREE.Vector3(-0.6, 0.35, 0.72).normalize();

/** A planet or moon. Placeholder art: swap this for the sky lane's bodies when they land. */
export function makeBody(kind: Kind, radius: number, seed: number): THREE.Object3D {
  const pal: Record<Kind, [number, number, number, number, number]> = {
    giant: [0xc9874b, 0xf0d2a0, 0x8a4a2a, 11, 0],
    ringed: [0xd9b77a, 0xa8794c, 0xf3e1b8, 16, 0],
    moon: [0x9a948c, 0x6e6a66, 0xc8c2b8, 2, 1],
    ice: [0x6d8fa8, 0xbcd3df, 0x3f5d78, 6, 0.3],
  };
  const [a, b, c, bands, craters] = pal[kind];
  const g = new THREE.Group();
  const mat = new THREE.ShaderMaterial({
    vertexShader: bodyVert, fragmentShader: bodyFrag, fog: false,
    uniforms: {
      uA: { value: new THREE.Color(a) }, uB: { value: new THREE.Color(b) }, uC: { value: new THREE.Color(c) },
      uBands: { value: bands }, uCraters: { value: craters }, uSunDir: { value: SUN }, uSeed: { value: seed }, uTime: { value: 0 },
    },
  });
  const m = new THREE.Mesh(new THREE.SphereGeometry(radius, 48, 32), mat);
  g.add(m);
  if (kind === 'ringed') {
    const ring = new THREE.Mesh(new THREE.PlaneGeometry(radius * 4.6, radius * 4.6), new THREE.ShaderMaterial({
      transparent: true, side: THREE.DoubleSide, depthWrite: false, fog: false,
      uniforms: { uA: { value: new THREE.Color(0xe8d2a6) }, uB: { value: new THREE.Color(0x8c6a48) } },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
      fragmentShader: ringFrag,
    }));
    ring.rotation.x = -Math.PI / 2 + 0.38; ring.rotation.z = 0.25;
    g.add(ring);
  }
  g.rotation.z = (seed % 1) * 0.5 - 0.25;
  return g;
}

interface Body { o: THREE.Object3D; z0: number; speed: number; spin: number; billboard: boolean }

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';

/** The Godot game's painted planets and moons (the "Day to night sky" lane's 4x2 atlas), as lit discs. */
const discFrag = /* glsl */`
  uniform sampler2D uAtlas; uniform vec4 uCell; uniform vec3 uSunDir; uniform float uAtmo;
  varying vec2 vUv;
  void main() {
    vec2 p = vUv * 2.0 - 1.0; float r2 = dot(p, p);
    if (r2 > 1.0) discard;
    vec4 tex = texture2D(uAtlas, uCell.xy + vUv * uCell.zw);
    vec3 nV = normalize(vec3(p * 1.04, sqrt(max(1.0 - r2 * 1.08, 0.0001))));
    vec3 L = normalize((viewMatrix * vec4(uSunDir, 0.0)).xyz + vec3(0.0, 0.0, 0.6));
    float lit = smoothstep(-0.3, 0.3, dot(nV, L));
    vec3 col = tex.rgb * mix(vec3(0.1, 0.11, 0.18), vec3(1.15), lit);
    col += vec3(0.55, 0.75, 1.0) * smoothstep(0.6, 1.0, sqrt(r2)) * lit * uAtmo * 0.5;
    float a = tex.a * smoothstep(1.0, 0.94, sqrt(r2));
    if (a < 0.01) discard;
    gl_FragColor = vec4(col, a);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }`;

function paintedBody(atlas: THREE.Texture, cell: number, radius: number, atmo: number): THREE.Object3D {
  const u0 = (cell % 4) / 4, v0 = 1 - (Math.floor(cell / 4) + 1) / 2;
  const m = new THREE.Mesh(new THREE.PlaneGeometry(radius * 2, radius * 2), new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, fog: false,
    uniforms: { uAtlas: { value: atlas }, uCell: { value: new THREE.Vector4(u0, v0, 0.25, 0.5) }, uSunDir: { value: SUN }, uAtmo: { value: atmo } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader: discFrag,
  }));
  m.renderOrder = -9;
  return m;
}

export class SpaceView {
  readonly group = new THREE.Group();
  private dome: THREE.ShaderMaterial;
  private bodies: Body[] = [];

  constructor(quality: number) {
    this.group.name = 'saloon-spaceview';
    this.group.visible = false;
    // star dome: three layers of stars, a faint violet-gold nebula, and the near layer streams aft
    this.dome = new THREE.ShaderMaterial({
      side: THREE.BackSide, fog: false, depthWrite: true,
      uniforms: { uTime: { value: 0 }, uSpeed: { value: SPEED / DOME_R } },
      vertexShader: 'varying vec3 vD; void main(){ vD = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
      fragmentShader: /* glsl */`
        uniform float uTime; uniform float uSpeed; varying vec3 vD;
        float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float n(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(h(i), h(i + vec2(1, 0)), f.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), f.x), f.y); }
        float stars(vec2 uv, float scale, float thr) {
          vec2 g = uv * scale; vec2 id = floor(g); vec2 f = fract(g) - 0.5;
          float r = h(id); if (r < thr) return 0.0;
          vec2 o = vec2(h(id + 3.1), h(id + 7.7)) - 0.5;
          return smoothstep(0.08, 0.0, length(f - o * 0.7)) * (r - thr) / (1.0 - thr);
        }
        void main() {
          vec3 d = normalize(vD);
          // longitude runs along the car (z) so the sky turns past the windows as the train runs
          float lon = atan(d.x, d.z), lat = asin(clamp(d.y, -1.0, 1.0));
          vec2 uv = vec2(lon, lat);
          vec3 col = mix(vec3(0.020, 0.028, 0.065), vec3(0.039, 0.055, 0.110), smoothstep(-0.6, 0.6, d.y));
          float neb = n(uv * 2.2 + vec2(uTime * uSpeed * 0.15, 0.0)) * n(uv * 5.0 - 3.0);
          col += vec3(0.28, 0.13, 0.30) * smoothstep(0.35, 0.8, neb) * 0.16;
          col += vec3(0.55, 0.32, 0.12) * smoothstep(0.35, 0.8, n(uv * 1.3 + 9.0)) * 0.05;
          col += vec3(0.85, 0.85, 1.0) * stars(uv + vec2(uTime * uSpeed * 0.2, 0.0), 70.0, 0.80) * 0.9;
          col += vec3(1.0, 0.85, 0.65) * stars(uv + vec2(uTime * uSpeed * 0.5, 0.0), 36.0, 0.89) * 1.4;
          col += vec3(1.0, 0.95, 0.9) * stars(uv + vec2(uTime * uSpeed, 0.0), 18.0, 0.94) * 2.0;
          gl_FragColor = vec4(col, 1.0);
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`,
    });
    const dome = new THREE.Mesh(new THREE.SphereGeometry(DOME_R, 48, 24), this.dome);
    dome.renderOrder = -10;
    dome.frustumCulled = false;
    this.group.add(dome);

    // the bodies: a big ringed giant and a gas giant well off each side, moons and an ice world nearer
    const spec: [Kind, number, number, number, number, number][] = [
      // kind, radius, x, y, z0, speed multiplier
      ['ringed', 20, -95, 16, -20, 0.35],
      ['giant', 26, 98, -10, 30, 0.25],
      ['moon', 6, -60, 5, 40, 1.0],
      ['ice', 10, 70, 24, -45, 0.6],
      ['moon', 3.6, 50, -8, 5, 1.4],
    ];
    const n = quality >= 1 ? spec.length : 3;
    for (let i = 0; i < n; i++) {
      const [kind, r, x, y, z0, sm] = spec[i];
      const o = makeBody(kind, r, i * 1.37 + 0.2);
      o.position.set(x, y + CAR.h * 0.5, z0);
      this.group.add(o);
      this.bodies.push({ o, z0, speed: SPEED * sm, spin: 0.02 + i * 0.01, billboard: false });
    }
    // the painted Godot planets replace the placeholders once their atlas is in (sky lane)
    new THREE.TextureLoader().loadAsync(BASE + 'assets/sky/celestial_atlas.webp').then((atlas) => {
      atlas.colorSpace = THREE.SRGBColorSpace;
      for (const b of this.bodies) b.o.removeFromParent();
      this.bodies.length = 0;
      // atlas cell, radius, x, y, z0, speed multiplier, atmosphere
      const painted: [number, number, number, number, number, number, number][] = [
        [0, 30, 100, 14, 10, 0.25, 0.55], // the gas giant, bar side
        [1, 9, -80, 22, -25, 0.5, 0], // the big tan moon, booth side
        [3, 11, -95, -6, 45, 0.35, 0.45], // the cream planet
        [4, 5, 70, 30, -50, 0.7, 0.3], // teal
        [7, 4, -60, 8, 70, 0.9, 0], // rust
        [2, 3.5, 55, -12, -10, 1.1, 0], // small moon
      ];
      const m = quality >= 1 ? painted.length : 3;
      for (let i = 0; i < m; i++) {
        const [cell, r, x, y, z0, sm, atmo] = painted[i];
        const o = paintedBody(atlas, cell, r, atmo);
        o.position.set(x, y, z0);
        this.group.add(o);
        this.bodies.push({ o, z0, speed: SPEED * sm, spin: 0, billboard: true });
      }
    }).catch(() => { /* no atlas on this branch yet: keep the placeholders */ });
  }

  update(dt: number, time: number, cam?: THREE.Camera) {
    if (!this.group.visible) return;
    this.dome.uniforms.uTime.value = time;
    for (const b of this.bodies) {
      // stream aft (+Z: the train runs fore) and wrap well out of sight behind the car
      let z = b.o.position.z + b.speed * dt;
      if (z > SPAN / 2) z -= SPAN;
      b.o.position.z = z;
      if (b.billboard && cam) b.o.quaternion.copy(cam.quaternion);
      else b.o.rotation.y += b.spin * dt;
    }
  }
}
